/* OwnerAdminTraffic — native website traffic analytics.
 * Reads /api/owner-admin/traffic/summary. Self-hosted, privacy-friendly;
 * staff visits excluded at ingest.
 */
import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarPicker } from '../ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  ComposedChart, Area, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
  CartesianGrid, BarChart, Cell,
} from 'recharts';
import {
  Globe, Users, Eye, RefreshCw, Loader2, Link2, Megaphone, MonitorSmartphone,
  DollarSign, Target, Hash, MousePointerClick, UserPlus, Calendar, TrendingUp,
  Clock, Repeat, User as UserIcon, MapPin, ArrowRight, Bot, ShieldCheck,
  ArrowUp, ArrowDown, ArrowUpDown, Search as SearchIcon, Volume2, VolumeX, Hand,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { days: 1, label: 'Today' },
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
];

// Traffic-type filter — see backend shared/traffic_classify.py.
const TRAFFIC_TYPES = [
  { value: 'human', label: 'Humans only' },
  { value: 'bot', label: 'Bots only' },
  { value: 'all', label: 'All (humans + bots)' },
];

// Friendly labels for bot_kind so operators know what they're looking at.
const BOT_KIND_LABEL = {
  ad: 'Ad platform',        // AdsBot-Google, facebookexternalhit, etc.
  search: 'Search crawler', // Googlebot, Bingbot
  preview: 'Emergent',      // Editor iframe / monitor
  automation: 'Automation', // Playwright, Puppeteer, Selenium
  cli: 'HTTP client',       // curl, python-requests
  unknown: 'Other bot',
};

// Format YYYY-MM-DD from a JS Date without any UTC-shifting.
function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function humanRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMin = Math.floor((Date.now() - then) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Drill-down modal — opens when the operator clicks any row in a
// Top list (Top pages / campaigns / referrers / keywords / gclids).
// Shows top visitors on that dimension + last 25 matching pageviews.
function TopDrillModal({ field, value, fieldLabel, open, onClose, trafficType, days, customRange, onOpenVisitor }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !field || !value) { setDetail(null); return; }
    let alive = true;
    setLoading(true);
    (async () => {
      const params = new URLSearchParams({ field, value, traffic_type: trafficType });
      if (customRange?.from && customRange?.to) {
        params.set('start_date', toISODate(customRange.from));
        params.set('end_date', toISODate(customRange.to));
      } else {
        params.set('days', String(days));
      }
      try {
        const r = await fetch(`${API}/api/owner-admin/traffic/drill?${params}`, { credentials: 'include' });
        if (r.ok && alive) setDetail(await r.json());
      } catch { /* modal shows error state */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [open, field, value, trafficType, days, customRange?.from, customRange?.to]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="top-drill-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <SearchIcon className="h-5 w-5 text-emerald-400" />
            <span className="text-muted-foreground text-sm mr-1">{fieldLabel}:</span>
            <span className="font-mono text-emerald-300">{value}</span>
          </DialogTitle>
          {detail && (
            <DialogDescription className="text-xs">
              {detail.totals.pageviews.toLocaleString()} pageviews · {detail.totals.unique_visitors} unique visitors
            </DialogDescription>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : !detail ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Could not load drill data.</p>
        ) : (
          <div className="overflow-y-auto space-y-4 pr-1">
            {/* Top visitors on this dimension */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                Top visitors on this {fieldLabel.toLowerCase()}
              </div>
              {detail.top_visitors.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No visitors yet.</p>
              ) : (
                <ul className="space-y-1">
                  {detail.top_visitors.map((v) => (
                    <li
                      key={v.visitor_id}
                      onClick={() => onOpenVisitor(v.visitor_id)}
                      className="flex items-center gap-3 text-xs py-1.5 px-2 rounded hover:bg-white/[0.04] cursor-pointer transition-colors"
                    >
                      <UserIcon className={`h-3 w-3 shrink-0 ${v.user_name ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                      <span className="truncate max-w-[45%] font-medium">
                        {v.user_name || <span className="font-mono text-[10px] text-muted-foreground">{v.visitor_id.slice(0, 14)}…</span>}
                      </span>
                      {v.user_email && (
                        <span className="text-[10px] text-muted-foreground truncate max-w-[30%]">{v.user_email}</span>
                      )}
                      <span className="ml-auto text-emerald-400 font-semibold">{v.pageviews} views</span>
                      <span className="text-muted-foreground text-[10px]">{humanRelative(v.last_seen)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Recent pageview stream */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                Recent pageviews · newest first
              </div>
              <ol className="space-y-1">
                {detail.recent_pageviews.map((pv, i) => (
                  <li key={i} className="flex items-center gap-3 text-xs py-1.5 border-b border-white/5 last:border-0">
                    <span className="text-muted-foreground w-20 shrink-0">{humanRelative(pv.created_at)}</span>
                    <span
                      onClick={() => onOpenVisitor(pv.visitor_id)}
                      className="font-mono text-[10px] text-muted-foreground hover:text-emerald-400 cursor-pointer truncate max-w-[25%]"
                      title="View this visitor"
                    >
                      {(pv.visitor_id || '').slice(0, 12)}…
                    </span>
                    <ArrowRight className="h-3 w-3 text-emerald-500/40 shrink-0" />
                    <span className="text-emerald-300 truncate max-w-[30%]">{pv.path}</span>
                    {pv.utm_campaign && (
                      <span className="ml-auto text-[10px] text-amber-300 shrink-0">{pv.utm_campaign}</span>
                    )}
                    {pv.is_paid && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 text-[9px] px-1.5 py-0.5 shrink-0">
                        <DollarSign className="h-2.5 w-2.5" /> paid
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Full path history + first-touch attribution modal for a single visitor.
// Opened from the Repeat Visitors table row-click.
function VisitorDrillModal({ visitorId, open, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !visitorId) { setDetail(null); return; }
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API}/api/owner-admin/traffic/visitor/${encodeURIComponent(visitorId)}`, { credentials: 'include' });
        if (r.ok && alive) setDetail(await r.json());
      } catch { /* keep loading state clear */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [open, visitorId]);

  const ft = detail?.first_touch;
  const user = detail?.user;
  const displayName = user?.name || `Anonymous · ${(visitorId || '').slice(0, 12)}…`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="visitor-drill-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <UserIcon className="h-5 w-5 text-emerald-400" />
            {displayName}
          </DialogTitle>
          {user?.email && (
            <DialogDescription className="text-xs">{user.email}</DialogDescription>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : !detail ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Could not load visitor detail.</p>
        ) : (
          <div className="overflow-y-auto space-y-4 pr-1">
            {/* First-touch card */}
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2 flex items-center gap-1.5">
                <MapPin className="h-3 w-3" /> First-touch attribution
              </div>
              {ft && (ft.utm_source || ft.gclid || ft.referrer_host || ft.landing_path) ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div>
                    <div className="text-[10px] text-muted-foreground uppercase">Landing</div>
                    <div className="font-medium text-emerald-300">{ft.landing_path || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-muted-foreground uppercase">Source</div>
                    <div className="font-medium">{ft.utm_source || ft.referrer_host || 'direct'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-muted-foreground uppercase">Campaign</div>
                    <div className="font-medium">{ft.utm_campaign || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-muted-foreground uppercase">Keyword</div>
                    <div className="font-medium truncate">{ft.utm_term || '—'}</div>
                  </div>
                  {ft.gclid && (
                    <div className="col-span-2 md:col-span-4">
                      <div className="text-[10px] text-muted-foreground uppercase">Google Click ID</div>
                      <div className="font-mono text-[10px] truncate">{ft.gclid}</div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Direct traffic — no ad or referrer captured.</p>
              )}
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="rounded border border-white/5 py-2">
                <div className="text-[10px] text-muted-foreground uppercase">Pageviews</div>
                <div className="text-lg font-bold text-emerald-400">{detail.totals.pageviews}</div>
              </div>
              <div className="rounded border border-white/5 py-2">
                <div className="text-[10px] text-muted-foreground uppercase">Distinct pages</div>
                <div className="text-lg font-bold">{detail.totals.distinct_paths}</div>
              </div>
              <div className="rounded border border-white/5 py-2">
                <div className="text-[10px] text-muted-foreground uppercase">First seen</div>
                <div className="text-xs font-medium mt-1">{humanRelative(detail.totals.first_seen)}</div>
              </div>
              <div className="rounded border border-white/5 py-2">
                <div className="text-[10px] text-muted-foreground uppercase">Last seen</div>
                <div className="text-xs font-medium mt-1">{humanRelative(detail.totals.last_seen)}</div>
              </div>
            </div>

            {/* Ordered path history */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                Full path history · oldest first
              </div>
              <ol className="space-y-1.5" data-testid="visitor-path-history">
                {detail.pageviews.map((pv, i) => (
                  <li key={i} className="flex items-center gap-3 text-xs py-1.5 border-b border-white/5 last:border-0">
                    <span className="text-[10px] font-mono text-muted-foreground w-6 shrink-0">{i + 1}.</span>
                    <span className="text-muted-foreground w-24 shrink-0">{humanRelative(pv.created_at)}</span>
                    <ArrowRight className="h-3 w-3 text-emerald-500/40 shrink-0" />
                    <span className="font-medium text-emerald-300 truncate max-w-[35%]">{pv.path}</span>
                    {pv.utm_source && (
                      <span className="text-[10px] text-amber-300 shrink-0 ml-auto">
                        {pv.utm_source}{pv.utm_campaign ? ` · ${pv.utm_campaign}` : ''}
                      </span>
                    )}
                    {!pv.utm_source && pv.referrer_host && (
                      <span className="text-[10px] text-muted-foreground shrink-0 ml-auto">
                        from {pv.referrer_host}
                      </span>
                    )}
                    {pv.is_paid && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 text-[9px] px-1.5 py-0.5 shrink-0">
                        <DollarSign className="h-2.5 w-2.5" /> paid
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Soft "ping" chime for the live-feed sound alert. Web-Audio-generated
// so we don't need to ship an asset. Two-note descending arpeggio, ~200ms.
function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    [880, 660].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t0 = now + i * 0.09;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.15, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.22);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.25);
    });
    setTimeout(() => ctx.close().catch(() => {}), 700);
  } catch { /* AudioContext blocked — silent no-op */ }
}

// Live visitor feed — polls /traffic/live every 15s so the operator can
// watch pageviews land in real time. Auto-collapses gracefully when nobody
// is on the site.
function LiveVisitorFeed({ trafficType = 'human' }) {
  const [live, setLive] = useState(null);
  const [ticking, setTicking] = useState(true);
  // Sound alert opt-in — muted by default (persisted in localStorage).
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem('cb_live_sound') === '1'; } catch { return false; }
  });
  // Tracks the newest event we've already surfaced so we only chime on NEW
  // paid-ad visitors that arrive after the operator opened the page.
  const lastSeenAtRef = useRef(null);

  useEffect(() => {
    let alive = true;
    let timer;
    const poll = async () => {
      try {
        const r = await fetch(
          `${API}/api/owner-admin/traffic/live?minutes=5&traffic_type=${trafficType}`,
          { credentials: 'include' },
        );
        if (r.ok && alive) {
          const data = await r.json();
          // Detect any new paid-ad event since our last poll and chime.
          if (soundOn && lastSeenAtRef.current && data.events?.length) {
            const isPaid = (e) => e.utm_source || e.utm_campaign;
            const newPaid = data.events.find(
              (e) => new Date(e.created_at) > lastSeenAtRef.current && isPaid(e),
            );
            if (newPaid) playChime();
          }
          if (data.events?.length) {
            lastSeenAtRef.current = new Date(data.events[0].created_at);
          }
          setLive(data);
        }
      } catch { /* keep last snapshot */ }
      if (alive && ticking) timer = setTimeout(poll, 15000);
    };
    poll();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [ticking, trafficType, soundOn]);

  const toggleSound = () => {
    setSoundOn((prev) => {
      const next = !prev;
      try { localStorage.setItem('cb_live_sound', next ? '1' : '0'); } catch { /* noop */ }
      if (next) playChime();  // audible confirmation of enable
      return next;
    });
  };

  const isEmpty = !live || live.pageviews === 0;

  return (
    <Card data-testid="traffic-live-feed" className="border-emerald-500/20">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className={`absolute inline-flex h-full w-full rounded-full ${isEmpty ? 'bg-zinc-500/40' : 'bg-emerald-400 animate-ping'} opacity-75`} />
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isEmpty ? 'bg-zinc-500' : 'bg-emerald-500'}`} />
          </span>
          Live visitors
          <span className="text-xs font-normal text-muted-foreground">
            {isEmpty
              ? 'No one on the site in the last 5 minutes'
              : `${live.active_visitors} active · ${live.pageviews} pageviews · last 5 min`}
          </span>
          <button
            type="button"
            onClick={toggleSound}
            className={`ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest ${soundOn ? 'text-emerald-400' : 'text-muted-foreground'} hover:text-emerald-400`}
            title={soundOn ? 'Mute paid-ad chime' : 'Play a chime when a paid-ad visitor lands'}
            data-testid="traffic-live-sound-toggle"
          >
            {soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
            {soundOn ? 'Chime on' : 'Chime off'}
          </button>
          <button
            type="button"
            onClick={() => setTicking((t) => !t)}
            className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-emerald-400"
            data-testid="traffic-live-toggle"
          >
            {ticking ? 'Pause' : 'Resume'}
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isEmpty ? (
          <p className="text-xs text-muted-foreground py-2">
            Live pageviews will appear here as visitors land on your marketing pages.
          </p>
        ) : (
          <ul className="divide-y divide-white/5 max-h-56 overflow-y-auto">
            {live.events.slice(0, 20).map((e, i) => (
              <li key={`${e.visitor_id}-${e.created_at}-${i}`} className="py-1.5 flex items-center gap-3 text-xs">
                <span className="text-muted-foreground w-16 shrink-0">{humanRelative(e.created_at)}</span>
                <span className="truncate max-w-[35%] font-medium">
                  {e.user_name ? (
                    <span className="text-emerald-400">
                      <UserIcon className="inline w-3 h-3 mr-1" />
                      {e.user_name}
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {(e.visitor_id || '').slice(0, 12)}…
                    </span>
                  )}
                </span>
                <span className="text-emerald-300 truncate max-w-[25%]">{e.path}</span>
                {e.traffic_type === 'bot' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-300 text-[9px] px-1.5 py-0.5 shrink-0">
                    <Bot className="h-2.5 w-2.5" />
                    {BOT_KIND_LABEL[e.bot_kind] || 'Bot'}
                  </span>
                )}
                {e.utm_source && (
                  <span className="ml-auto text-[10px] text-muted-foreground shrink-0">
                    from <span className="text-amber-300">{e.utm_source}</span>
                    {e.utm_campaign && <span className="text-muted-foreground"> · {e.utm_campaign}</span>}
                  </span>
                )}
                {!e.utm_source && e.referrer_host && (
                  <span className="ml-auto text-[10px] text-muted-foreground shrink-0">from {e.referrer_host}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Icon className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TopList({ title, icon: Icon, rows, emptyLabel, testId, onRowClick }) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="h-4 w-4 text-emerald-400" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4">{emptyLabel}</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div
                key={r.value}
                className={`text-xs ${onRowClick ? 'cursor-pointer hover:bg-white/[0.04] -mx-2 px-2 py-1 rounded transition-colors' : ''}`}
                onClick={onRowClick ? () => onRowClick(r.value) : undefined}
              >
                <div className="flex justify-between mb-1">
                  <span className="truncate max-w-[70%] font-medium">{r.value}</span>
                  <span className="text-muted-foreground">{r.count} views · {r.visitors} visitors</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/5">
                  <div className="h-1.5 rounded-full bg-emerald-500/60" style={{ width: `${(r.count / max) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Compact list for CONVERSIONS attributed to a campaign — shows count and,
// when available, revenue. Empty state is copy-tuned for onboarding.
function ConversionList({ title, icon: Icon, rows, emptyLabel, testId, unit }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="h-4 w-4 text-emerald-400" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4">{emptyLabel}</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.value} className="flex items-center justify-between text-xs">
                <span className="truncate max-w-[55%] font-medium">{r.value}</span>
                <span className="text-muted-foreground shrink-0">
                  {r.count} {unit}
                  {typeof r.revenue === 'number' && r.revenue > 0 && (
                    <span className="ml-2 font-semibold text-emerald-400">
                      ${r.revenue.toFixed(2)}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function OwnerAdminTraffic() {
  const [days, setDays] = useState(30);
  // customRange is { from: Date, to: Date } — when set, overrides `days`.
  const [customRange, setCustomRange] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Currently-drilled visitor id (opens the drill-through modal).
  const [drillVisitorId, setDrillVisitorId] = useState(null);
  // Top-list drill-down state — { field, value, fieldLabel } opens the modal.
  const [drillDim, setDrillDim] = useState(null);
  // Repeat-visitors table sort state — key + direction ('asc'|'desc').
  const [rvSort, setRvSort] = useState({ key: 'pageviews', dir: 'desc' });
  // Traffic-type filter — 'human' (default), 'bot', or 'all'. Passed to the
  // backend so the operator sees clean numbers by default and can toggle
  // to inspect Googlebot / AdsBot / Emergent monitor traffic separately.
  const [trafficType, setTrafficType] = useState('human');

  // Browser timezone → passed to backend for hour-of-day bucketing so the
  // "Traffic by hour" chart matches what the operator considers "9am".
  const tz = (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'America/Los_Angeles';

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('tz', tz);
      params.set('traffic_type', opts.trafficType ?? trafficType);
      if (opts.range) {
        params.set('start_date', toISODate(opts.range.from));
        params.set('end_date', toISODate(opts.range.to));
      } else {
        params.set('days', String(opts.days ?? days));
      }
      const r = await fetch(`${API}/api/owner-admin/traffic/summary?${params}`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    } catch { /* handled by data===null render */ }
    setLoading(false);
  };

  useEffect(() => {
    if (customRange?.from && customRange?.to) load({ range: customRange });
    else load({ days });
  }, [days, customRange?.from?.getTime(), customRange?.to?.getTime(), trafficType]);

  const rangeLabel = customRange?.from && customRange?.to
    ? `${toISODate(customRange.from)} → ${toISODate(customRange.to)}`
    : 'Custom range';

  return (
    <div className="space-y-6" data-testid="owner-admin-traffic">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6 text-emerald-400" /> Website Traffic
          </h1>
          <p className="text-sm text-muted-foreground">
            Self-hosted analytics for the public marketing pages. Staff visits are excluded automatically.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={trafficType} onValueChange={setTrafficType}>
            <SelectTrigger className="h-9 w-[172px]" data-testid="traffic-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRAFFIC_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {RANGES.map((r) => (
            <Button
              key={r.days}
              size="sm"
              variant={!customRange && days === r.days ? 'default' : 'outline'}
              onClick={() => { setCustomRange(null); setDays(r.days); }}
              data-testid={`traffic-range-${r.days}`}
            >
              {r.label}
            </Button>
          ))}
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant={customRange ? 'default' : 'outline'}
                data-testid="traffic-range-custom"
                className="gap-1.5"
              >
                <Calendar className="h-3.5 w-3.5" />
                {rangeLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0" data-testid="traffic-range-picker">
              <CalendarPicker
                mode="range"
                selected={customRange || undefined}
                onSelect={(r) => {
                  setCustomRange(r || null);
                  if (r?.from && r?.to) setPickerOpen(false);
                }}
                numberOfMonths={2}
                initialFocus
              />
              <div className="flex items-center justify-between border-t p-2 gap-2">
                <span className="text-[11px] text-muted-foreground pl-2">
                  Pick a start and end date
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setCustomRange(null); setPickerOpen(false); }}
                  data-testid="traffic-range-clear"
                >
                  Clear
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          <Button size="sm" variant="ghost" onClick={() => load()} data-testid="traffic-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : !data ? (
        <p className="text-sm text-muted-foreground">Could not load traffic data.</p>
      ) : (
        <>
          <LiveVisitorFeed trafficType={trafficType} />

          {/* Traffic composition banner — always shows the FULL window
              breakdown (ignoring the current filter) so the operator
              knows exactly what they're excluding. */}
          {data.traffic_breakdown && (
            <Card data-testid="traffic-breakdown-card" className="border-white/10">
              <CardContent className="py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Traffic mix in window
                </span>
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                  <span className="font-semibold">{data.traffic_breakdown.human.toLocaleString()}</span>
                  <span className="text-muted-foreground">humans</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <Bot className="h-3.5 w-3.5 text-amber-400" />
                  <span className="font-semibold">{data.traffic_breakdown.bot.toLocaleString()}</span>
                  <span className="text-muted-foreground">bots</span>
                </span>
                {Object.entries(data.traffic_breakdown.by_bot_kind || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, n]) => (
                    <span key={k} className="text-[11px] text-muted-foreground">
                      <span className="text-amber-300">{BOT_KIND_LABEL[k] || k}</span>: {n}
                    </span>
                  ))}
                {trafficType !== 'all' && (
                  <span className="ml-auto text-[10px] italic text-muted-foreground">
                    Filter: <span className="text-emerald-300">
                      {TRAFFIC_TYPES.find((t) => t.value === trafficType)?.label}
                    </span>
                  </span>
                )}
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard icon={Eye} label="Pageviews" value={data.totals.pageviews.toLocaleString()} testId="traffic-stat-pageviews" />
            <StatCard icon={Users} label="Unique visitors" value={data.totals.unique_visitors.toLocaleString()} testId="traffic-stat-visitors" />
            <StatCard icon={Globe} label="Avg views / day" value={data.totals.avg_per_day} testId="traffic-stat-avg" />
            <StatCard
              icon={DollarSign}
              label="Paid pageviews"
              value={(data.totals.paid_pageviews || 0).toLocaleString()}
              testId="traffic-stat-paid"
            />
            <StatCard
              icon={Repeat}
              label="Repeat visitors"
              value={(data.totals.repeat_visitors || 0).toLocaleString()}
              testId="traffic-stat-repeat"
            />
          </div>

          <Card data-testid="traffic-chart-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Daily traffic</CardTitle>
            </CardHeader>
            <CardContent>
              {data.daily.length === 0 ? (
                <p className="text-xs text-muted-foreground py-8 text-center">
                  No pageviews recorded yet — data starts flowing as soon as visitors hit the landing pages.
                </p>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data.daily}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', fontSize: 12 }} />
                      <Area type="monotone" dataKey="pageviews" name="Pageviews" stroke="#10b981" fill="rgba(16,185,129,0.15)" strokeWidth={2} />
                      <Bar dataKey="visitors" name="Visitors" fill="rgba(56,189,248,0.5)" radius={[3, 3, 0, 0]} barSize={14} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Hour-of-day distribution — helps you decide the best window to
              push ads / launch campaigns. Bucketed in the operator's local
              timezone so 9am really means 9am. */}
          <Card data-testid="traffic-hourly-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Clock className="h-4 w-4 text-emerald-400" /> Traffic by hour of day
                <span className="text-xs font-normal text-muted-foreground">
                  {(data.range?.tz || tz)} · 24-hour local time
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(data.hourly || []).every((h) => !h.pageviews) ? (
                <p className="text-xs text-muted-foreground py-8 text-center">
                  Not enough data yet to show peak hours.
                </p>
              ) : (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.hourly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', fontSize: 12 }}
                        labelFormatter={(l) => `${l} local`}
                      />
                      <Bar dataKey="pageviews" name="Pageviews" radius={[3, 3, 0, 0]}>
                        {data.hourly.map((h, i) => (
                          <Cell key={i} fill="#10b981" fillOpacity={0.35 + Math.min(0.55, h.pageviews / Math.max(1, Math.max(...data.hourly.map((x) => x.pageviews))) * 0.55)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Repeat visitors — anyone who came back for 2+ pageviews in the
              current range. Where possible we resolve the visitor_id to a
              real customer name via the /users join on backend. */}
          <Card data-testid="traffic-repeat-visitors-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Repeat className="h-4 w-4 text-emerald-400" /> Repeat visitors
                <span className="text-xs font-normal text-muted-foreground">
                  Visitors with 2+ pageviews in this range
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(data.repeat_visitors || []).length === 0 ? (
                <p className="text-xs text-muted-foreground py-6 text-center">
                  No repeat visitors in this window yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="traffic-repeat-visitors-table">
                    <thead className="text-muted-foreground border-b border-white/5 select-none">
                      <tr>
                        {[
                          { key: 'name', label: 'Visitor', align: 'left' },
                          { key: 'pageviews', label: 'Views', align: 'right' },
                          { key: 'paths_count', label: 'Pages', align: 'right' },
                          { key: 'first_seen', label: 'First seen', align: 'right' },
                          { key: 'last_seen', label: 'Last seen', align: 'right' },
                        ].map((col) => {
                          const active = rvSort.key === col.key;
                          const Icon = active ? (rvSort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                          return (
                            <th
                              key={col.key}
                              className={`py-2 px-3 font-medium cursor-pointer hover:text-emerald-400 transition-colors text-${col.align} ${col.align === 'left' ? 'pl-0 pr-3' : ''} ${col.key === 'last_seen' ? 'pl-3 pr-0' : ''}`}
                              onClick={() => setRvSort((s) => ({
                                key: col.key,
                                dir: s.key === col.key && s.dir === 'desc' ? 'asc' : 'desc',
                              }))}
                              data-testid={`rv-header-${col.key}`}
                            >
                              <span className="inline-flex items-center gap-1">
                                {col.label}
                                <Icon className={`h-3 w-3 ${active ? 'text-emerald-400' : 'text-muted-foreground/50'}`} />
                              </span>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {[...data.repeat_visitors].sort((a, b) => {
                        const dir = rvSort.dir === 'asc' ? 1 : -1;
                        if (rvSort.key === 'name') {
                          const an = (a.user_name || a.visitor_id || '').toLowerCase();
                          const bn = (b.user_name || b.visitor_id || '').toLowerCase();
                          return an.localeCompare(bn) * dir;
                        }
                        if (rvSort.key === 'first_seen' || rvSort.key === 'last_seen') {
                          return (new Date(a[rvSort.key]).getTime() - new Date(b[rvSort.key]).getTime()) * dir;
                        }
                        return ((a[rvSort.key] || 0) - (b[rvSort.key] || 0)) * dir;
                      }).map((v) => (
                        <tr
                          key={v.visitor_id}
                          onClick={() => setDrillVisitorId(v.visitor_id)}
                          className="border-b border-white/5 last:border-0 cursor-pointer hover:bg-white/[0.03] transition-colors"
                          data-testid={`traffic-repeat-row-${v.visitor_id}`}
                        >
                          <td className="py-2 pr-3">
                            {v.user_name ? (
                              <div className="flex items-center gap-2">
                                <UserIcon className="h-3.5 w-3.5 text-emerald-400" />
                                <div>
                                  <div className="font-medium">{v.user_name}</div>
                                  {v.user_email && (
                                    <div className="text-[10px] text-muted-foreground">{v.user_email}</div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="font-mono text-[11px] text-muted-foreground">
                                {v.visitor_id.slice(0, 14)}…
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right font-semibold text-emerald-400">{v.pageviews}</td>
                          <td className="py-2 px-3 text-right text-muted-foreground">{v.paths_count}</td>
                          <td className="py-2 px-3 text-right text-muted-foreground">{humanRelative(v.first_seen)}</td>
                          <td className="py-2 pl-3 text-right text-muted-foreground">{humanRelative(v.last_seen)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TopList title="Top pages" icon={Eye} rows={data.top_pages} emptyLabel="No pageviews yet." testId="traffic-top-pages"
              onRowClick={(v) => setDrillDim({ field: 'path', value: v, fieldLabel: 'Page' })} />
            <TopList title="Top referrers" icon={Link2} rows={data.top_referrers} emptyLabel="No external referrers yet — direct traffic only." testId="traffic-top-referrers"
              onRowClick={(v) => setDrillDim({ field: 'referrer_host', value: v, fieldLabel: 'Referrer' })} />
            <TopList title="UTM sources" icon={Megaphone} rows={data.top_utm_sources} emptyLabel="No UTM-tagged campaigns yet." testId="traffic-top-utm"
              onRowClick={(v) => setDrillDim({ field: 'utm_source', value: v, fieldLabel: 'UTM source' })} />
            <TopList title="Devices" icon={MonitorSmartphone} rows={data.devices} emptyLabel="No data yet." testId="traffic-devices"
              onRowClick={(v) => setDrillDim({ field: 'device', value: v, fieldLabel: 'Device' })} />
          </div>

          {/* ── Ad ROI panel — paid-traffic performance by campaign, keyword,
              and gclid. Populated by the UTM/gclid capture in TrafficTracker.
              Empty until visitors arrive from paid ads. */}
          <div className="pt-2">
            <h2 className="text-lg font-bold flex items-center gap-2 mb-3">
              <DollarSign className="h-5 w-5 text-emerald-400" /> Ad ROI
              <span className="text-xs font-normal text-muted-foreground">
                Paid-traffic performance by campaign · keyword · click ID
              </span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <TopList
                title="Top campaigns"
                icon={Target}
                rows={data.top_campaigns || []}
                emptyLabel="No utm_campaign traffic yet — tag your ads with ?utm_campaign=... to populate."
                testId="traffic-top-campaigns"
                onRowClick={(v) => setDrillDim({ field: 'utm_campaign', value: v, fieldLabel: 'Campaign' })}
              />
              <TopList
                title="Top keywords"
                icon={Hash}
                rows={data.top_terms || []}
                emptyLabel="No utm_term traffic yet — Google Ads auto-populates this on paid clicks."
                testId="traffic-top-terms"
                onRowClick={(v) => setDrillDim({ field: 'utm_term', value: v, fieldLabel: 'Keyword' })}
              />
              <TopList
                title="Top gclids"
                icon={MousePointerClick}
                rows={data.top_gclids || []}
                emptyLabel="No gclid clicks yet — arrives on every Google Ads click."
                testId="traffic-top-gclids"
                onRowClick={(v) => setDrillDim({ field: 'gclid', value: v, fieldLabel: 'Google Click ID' })}
              />
            </div>
          </div>

          {/* ── Conversions by campaign — signups + paid bookings joined to
              the ad that drove them via `attribution.utm_campaign` on the
              user / appointment doc. Revenue is shown in green next to each
              row when available (only appointments carry a paid amount at
              this level; subscription revenue joins via user attribution). */}
          <div className="pt-2">
            <h2 className="text-lg font-bold flex items-center gap-2 mb-3">
              <Target className="h-5 w-5 text-emerald-400" /> Conversions by campaign
              <span className="text-xs font-normal text-muted-foreground">
                Signups + paid bookings attributed to the ad that drove them
              </span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ConversionList
                title="Signups by campaign"
                icon={UserPlus}
                rows={data.signups_by_campaign || []}
                emptyLabel="No attributed signups yet — new signups from tagged ads will appear here."
                testId="traffic-signups-by-campaign"
                unit="signups"
              />
              <ConversionList
                title="Bookings by campaign"
                icon={Calendar}
                rows={data.bookings_by_campaign || []}
                emptyLabel="No attributed bookings yet — paid calls from tagged ads will appear here (with revenue)."
                testId="traffic-bookings-by-campaign"
                unit="bookings"
              />
            </div>
          </div>

          {/* ── Top clicks — what visitors actually pressed on the site.
              Uses the same TopList drill affordance as the other panels. */}
          <TopList
            title="Top clicks"
            icon={Hand}
            rows={data.top_clicks || []}
            emptyLabel="No click data yet — data starts flowing as soon as visitors interact with buttons or links on marketing pages."
            testId="traffic-top-clicks"
          />

          {/* ── Revenue per keyword — sorted bar chart of paid-booking revenue
              grouped by `attribution.utm_term`. Winners bubble to the top so
              the operator can double-down on high-ROI keywords and pause
              the ones that don't convert. */}
          <Card data-testid="traffic-revenue-per-keyword">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-400" /> Revenue per keyword
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  Top {(data.revenue_by_term || []).length || 12} keywords by booking revenue
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(data.revenue_by_term || []).length === 0 ? (
                <p className="text-xs text-muted-foreground py-8 text-center">
                  No revenue attributed to a keyword yet. Tag your ads with
                  <code className="mx-1 px-1 rounded bg-white/10">?utm_term=...</code>
                  (Google Ads auto-populates this on paid clicks) and paid
                  bookings will start flowing in here with dollar amounts.
                </p>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={data.revenue_by_term}
                      layout="vertical"
                      margin={{ top: 4, right: 30, left: 10, bottom: 4 }}
                    >
                      <CartesianGrid horizontal={false} stroke="rgba(255,255,255,0.06)" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v) => `$${v}`}
                      />
                      <YAxis
                        dataKey="value"
                        type="category"
                        tick={{ fontSize: 11 }}
                        width={160}
                        interval={0}
                      />
                      <Tooltip
                        contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', fontSize: 12 }}
                        formatter={(v, k, entry) => {
                          if (k === 'revenue') return [`$${Number(v).toFixed(2)}`, 'Revenue'];
                          return [v, k];
                        }}
                        labelFormatter={(label, items) => {
                          const bookings = items?.[0]?.payload?.count;
                          return bookings != null ? `${label} · ${bookings} booking${bookings === 1 ? '' : 's'}` : label;
                        }}
                      />
                      <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                        {data.revenue_by_term.map((_, i) => {
                          // Top bar bright emerald; taper opacity down the list
                          // so the winners visually pop.
                          const opacity = 1 - Math.min(0.65, i * 0.08);
                          return <Cell key={i} fill="#10b981" fillOpacity={opacity} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <VisitorDrillModal
        visitorId={drillVisitorId}
        open={!!drillVisitorId}
        onClose={() => setDrillVisitorId(null)}
      />
      <TopDrillModal
        field={drillDim?.field}
        value={drillDim?.value}
        fieldLabel={drillDim?.fieldLabel || ''}
        open={!!drillDim}
        onClose={() => setDrillDim(null)}
        trafficType={trafficType}
        days={days}
        customRange={customRange}
        onOpenVisitor={(vid) => { setDrillDim(null); setDrillVisitorId(vid); }}
      />
    </div>
  );
}
